import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaCheck, FaArrowRight } from 'react-icons/fa';
import { services } from '../data/services';
import {
  WebDevelopment,
  Maintenance,
  Cybersecurity,
  MobileApps,
  IconWrapper
} from '../design-system/icons';

const Services = () => {
  const getServiceIcon = (iconName, size = 40) => {
    const icons = {
      WebDevelopment: <WebDevelopment size={size} />,
      Maintenance: <Maintenance size={size} />,
      Cybersecurity: <Cybersecurity size={size} />,
      MobileApps: <MobileApps size={size} />,
    };
    return icons[iconName] || <WebDevelopment size={size} />;
  };

  return (
    <>
      <Helmet>
        <title>Nos Services | Création Web, SEO, Apps Mobiles | Olney Tech Solutions</title>
        <meta name="description" content="Découvrez tous nos services : création de sites web, référencement SEO, applications mobiles, cybersécurité, automatisation IA. Agence web à Lille." />
        <link rel="canonical" href="https://olney.dev/services" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[var(--primary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--primary)] bg-[rgba(112,0,255,0.1)] text-[var(--primary)] text-sm font-bold tracking-widest uppercase">
              Nos Expertises
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk'] text-white">
              Solutions Digitales <span className="text-gradient-electric">Complètes</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              De la création de votre site web à l'automatisation de vos processus,
              nous vous accompagnons dans toutes les étapes de votre transformation digitale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <IconWrapper size="lg" variant={service.color} glow={true}>
                      {getServiceIcon(service.icon, 40)}
                    </IconWrapper>
                    <div>
                      <span className={`text-[var(--${service.color})] text-sm font-bold uppercase tracking-widest`}>
                        Service
                      </span>
                      <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Space_Grotesk']">
                        {service.title}
                      </h2>
                    </div>
                  </div>

                  <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                    {service.fullDescription}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <FaCheck className={`text-[var(--${service.color})] mt-1 flex-shrink-0`} />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <Link
                      to={`/services/${service.slug}`}
                      className={`btn-magnetic px-6 py-3 bg-[var(--${service.color})] text-white font-bold rounded-xl hover:opacity-90 transition-all inline-flex items-center gap-2`}
                    >
                      En savoir plus <FaArrowRight />
                    </Link>
                    <Link
                      to="/contact"
                      className={`px-6 py-3 border border-[var(--${service.color})] text-[var(--${service.color})] font-bold rounded-xl hover:bg-[var(--${service.color})] hover:text-white transition-all`}
                    >
                      Demander un devis
                    </Link>
                  </div>
                </div>

                {/* Visual Card */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className={`glass-panel rounded-3xl p-8 border-[var(--${service.color})]/30 hover:border-[var(--${service.color})] transition-colors duration-500`}>
                    {/* Benefits */}
                    <h3 className="text-xl font-bold text-white mb-6 font-['Space_Grotesk']">
                      Avantages clés
                    </h3>
                    <div className="space-y-6 mb-8">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className={`w-10 h-10 rounded-full bg-[var(--${service.color})]/20 flex items-center justify-center flex-shrink-0`}>
                            <span className="text-lg">✨</span>
                          </div>
                          <div>
                            <h4 className="text-white font-bold mb-1">{benefit.title}</h4>
                            <p className="text-gray-400 text-sm">{benefit.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Technologies */}
                    <div className="border-t border-white/10 pt-6">
                      <p className="text-gray-500 text-sm uppercase tracking-wider mb-3">Technologies</p>
                      <div className="flex flex-wrap gap-2">
                        {service.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className={`px-3 py-1 rounded-full bg-[var(--${service.color})]/10 border border-[var(--${service.color})]/30 text-[var(--${service.color})] text-xs font-medium`}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Pricing Hint */}
                    <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">À partir de</span>
                        <span className={`text-[var(--${service.color})] font-bold text-xl`}>
                          {service.pricing.starting}{service.pricing.unit}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-panel rounded-3xl p-12 text-center border-[var(--primary)]/30"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
              Vous ne savez pas par où commencer ?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Discutons de votre projet ensemble. Je vous conseille gratuitement
              sur la meilleure stratégie pour atteindre vos objectifs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="btn-magnetic px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl hover:opacity-90 transition-all"
              >
                Prendre rendez-vous gratuit
              </Link>
              <a
                href="tel:+33689177385"
                className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
              >
                Appeler maintenant
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;
